import React, { useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { payrollService } from '../../../services/payrollService';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const TaxManagement = ({ taxConfig, onRefresh }) => {
  const { currentTenant, isGymOwner } = useAuth();
  const [editingConfig, setEditingConfig] = useState(null);
  const [saving, setSaving] = useState(false);

  const taxCategories = [
    { value: 'federal_tax', label: 'Federal Tax', icon: 'Building2' },
    { value: 'state_tax', label: 'State Tax', icon: 'MapPin' },
    { value: 'local_tax', label: 'Local Tax', icon: 'Home' },
    { value: 'social_security', label: 'Social Security', icon: 'Shield' },
    { value: 'medicare', label: 'Medicare', icon: 'Heart' }
  ];

  const handleEditConfig = (config) => {
    setEditingConfig({ ...config });
  };

  const handleSaveConfig = async () => {
    if (!editingConfig) return;

    setSaving(true);
    try {
      await payrollService?.updateTaxConfiguration(editingConfig?.id, {
        rate: editingConfig?.rate,
        threshold_amount: editingConfig?.threshold_amount,
        max_amount: editingConfig?.max_amount,
        is_active: editingConfig?.is_active
      });
      alert('Tax configuration updated successfully');
      setEditingConfig(null);
      onRefresh();
    } catch (error) {
      console.error('Error saving tax config:', error);
      alert('Error saving tax configuration');
    } finally {
      setSaving(false);
    }
  };

  const getCategoryIcon = (category) => {
    const cat = taxCategories?.find(c => c?.value === category);
    return cat?.icon || 'DollarSign';
  };

  const getCategoryLabel = (category) => {
    const cat = taxCategories?.find(c => c?.value === category);
    return cat?.label || category;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-foreground">Tax Configuration & Deduction Management</h2>
        {!isGymOwner && (
          <span className="text-sm text-yellow-600 bg-yellow-50 px-3 py-1 rounded-full">
            View Only - Owner Access Required
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {taxCategories?.map((category) => {
          const config = taxConfig?.find(c => c?.tax_category === category?.value);
          
          return (
            <div key={category?.value} className="border border-border rounded-lg p-4">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Icon name={category?.icon} size={20} className="text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground">{category?.label}</h3>
                    <p className="text-xs text-muted-foreground">{category?.value}</p>
                  </div>
                </div>
                {config && (
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    config?.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                  }`}>
                    {config?.is_active ? 'Active' : 'Inactive'}
                  </span>
                )}
              </div>

              {config ? (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-muted/50 rounded-lg p-3">
                      <p className="text-xs text-muted-foreground mb-1">Tax Rate</p>
                      <p className="text-lg font-semibold text-foreground">{config?.rate}%</p>
                    </div>
                    <div className="bg-muted/50 rounded-lg p-3">
                      <p className="text-xs text-muted-foreground mb-1">Max Amount</p>
                      <p className="text-lg font-semibold text-foreground">
                        {config?.max_amount ? `$${parseFloat(config?.max_amount)?.toFixed(2)}` : 'No Limit'}
                      </p>
                    </div>
                  </div>
                  {config?.threshold_amount && (
                    <div className="bg-muted/50 rounded-lg p-3">
                      <p className="text-xs text-muted-foreground mb-1">Threshold Amount</p>
                      <p className="text-sm font-medium text-foreground">
                        ${parseFloat(config?.threshold_amount)?.toFixed(2)}
                      </p>
                    </div>
                  )}
                  {isGymOwner && (
                    <Button
                      onClick={() => handleEditConfig(config)}
                      variant="outline"
                      className="w-full"
                    >
                      <Icon name="Edit" size={16} className="mr-2" />
                      Edit Configuration
                    </Button>
                  )}
                </div>
              ) : (
                <div className="text-center py-6">
                  <p className="text-sm text-muted-foreground mb-3">No configuration set</p>
                  {isGymOwner && (
                    <Button variant="outline" size="sm">
                      <Icon name="Plus" size={16} className="mr-1" />
                      Add Configuration
                    </Button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Edit Modal */}
      {editingConfig && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg max-w-md w-full">
            <div className="border-b border-border p-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-foreground">
                Edit {getCategoryLabel(editingConfig?.tax_category)}
              </h3>
              <button
                onClick={() => setEditingConfig(null)}
                className="text-muted-foreground hover:text-foreground"
              >
                <Icon name="X" size={24} />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Tax Rate (%)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={editingConfig?.rate}
                  onChange={(e) => setEditingConfig({ ...editingConfig, rate: parseFloat(e?.target?.value) || 0 })}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Threshold Amount ($)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={editingConfig?.threshold_amount || ''}
                  onChange={(e) => setEditingConfig({ ...editingConfig, threshold_amount: parseFloat(e?.target?.value) || null })}
                  placeholder="Optional"
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground"
                />
                <p className="text-xs text-muted-foreground mt-1">Minimum income before tax applies</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Maximum Amount ($)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={editingConfig?.max_amount || ''}
                  onChange={(e) => setEditingConfig({ ...editingConfig, max_amount: parseFloat(e?.target?.value) || null })}
                  placeholder="Optional"
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground"
                />
                <p className="text-xs text-muted-foreground mt-1">Maximum tax amount per period</p>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={editingConfig?.is_active}
                  onChange={(e) => setEditingConfig({ ...editingConfig, is_active: e?.target?.checked })}
                  className="w-4 h-4"
                />
                <label htmlFor="is_active" className="text-sm text-foreground">
                  Active
                </label>
              </div>
            </div>
            <div className="border-t border-border p-4 flex gap-3">
              <Button
                onClick={() => setEditingConfig(null)}
                variant="outline"
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveConfig}
                disabled={saving}
                className="flex-1"
              >
                {saving ? (
                  <Icon name="Loader2" size={18} className="animate-spin mr-2" />
                ) : (
                  <Icon name="Save" size={18} className="mr-2" />
                )}
                Save Changes
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Tax Calculation Info */}
      <div className="border border-border rounded-lg p-4 bg-muted/30">
        <div className="flex items-start gap-3">
          <Icon name="Info" size={20} className="text-primary mt-0.5" />
          <div>
            <h4 className="font-medium text-foreground mb-2">Tax Calculation Information</h4>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li>• Tax rates are applied automatically during payroll processing</li>
              <li>• Threshold amounts determine when tax starts applying</li>
              <li>• Maximum amounts cap the total tax per pay period</li>
              <li>• Inactive configurations are not applied to calculations</li>
              <li>• Changes take effect on next payroll processing</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaxManagement;